import AccountAvatar from "./AccountAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Props = {
  email?: string;
  /** Merchant display name. Only shown collapsed — expanded, the rail header carries it. */
  displayName?: string;
  isExpanded: boolean;
};

// The "who am I signed in as" row at the head of the rail's account cluster.
// Collapsed there is only room for the avatar, so the email lives in a tooltip;
// expanded it reads inline, no hover required.
const SideMenuAccount = ({ email, displayName, isExpanded }: Props): JSX.Element => {
  if (!email) {
    return isExpanded ? (
      <div className="flex items-center gap-2">
        <Skeleton className="size-8 shrink-0 rounded-full" />
        <Skeleton className="h-3.5 min-w-0 flex-1" />
      </div>
    ) : (
      <Skeleton className="mx-auto size-8 rounded-full" />
    );
  }

  if (isExpanded) {
    return (
      <div className="flex items-start gap-2">
        <AccountAvatar email={email} />
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">Sesión iniciada como</p>
          {/* Wrapping, not truncating: the address is the whole point of the row. */}
          <p className="break-all text-sm font-medium leading-snug text-palomma-secondary">
            {email}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          tabIndex={0}
          className="mx-auto w-fit rounded-full outline-none focus-visible:ring-2 focus-visible:ring-palomma-primary focus-visible:ring-offset-2"
        >
          <AccountAvatar email={email} />
          <span className="sr-only">Sesión iniciada como {email}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[240px]">
        <p className="text-xs text-muted-foreground">Sesión iniciada como</p>
        <p className="break-all font-medium">{email}</p>
        {displayName && (
          <p className="break-words text-xs text-muted-foreground">{displayName}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

export default SideMenuAccount;
